import React from "react";
import { Box, Typography, Link, Grid } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Link as RouterLink } from "react-router-dom";
import { getPageRoute } from "src/services";
import StanzaLogo from "src/assets/svg/stanza-logo.js";

/** Internal URLs render as router anchors (crawlable href + SPA nav); external as plain anchors. */
const linkPropsFor = (url) =>
  url.startsWith("http")
    ? { href: url, target: "_blank", rel: "noopener noreferrer" }
    : { component: RouterLink, to: url };

const ExpandedFooter = ({ variant = "light" }) => {
  const theme = useTheme();
  const isDark = variant === "dark";

  const footerColumns = [
    {
      title: "Browse Calendars",
      links: [
        { label: "All Calendars", url: getPageRoute("home", "HOME") },
        { label: "Sports Calendars", url: getPageRoute("home", "TAG", { tag: "sports" }) },
        { label: "NFL Schedule", url: getPageRoute("home", "TAG", { tag: "nfl" }) },
        { label: "NBA Schedule", url: getPageRoute("home", "TAG", { tag: "nba" }) },
        { label: "Women's Sports", url: getPageRoute("home", "TAG", { tag: "womens-sports" }) },
        { label: "Netflix Calendars", url: getPageRoute("home", "TAG", { tag: "netflix" }) },
      ],
    },
    {
      title: "Features",
      links: [
        { label: "Groups", url: "/product/groups" },
        { label: "Unlimited calendars", url: "/product/unlimited-calendars" },
        { label: "Cross-device sync", url: "/product/cross-device-sync" },
        { label: "Near real-time updates", url: "/product/real-time-updates" },
        { label: "Refresh on demand", url: "/product/force-refresh" },
        { label: "How Stanza works", url: "/product/how-stanza-gets-schedules" },
        { label: "Transparent Updates", url: "/product/transparent-updates" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", url: "https://stanza-1.gitbook.io/stanza-help-center/stanza-overview/quickstart" },
        { label: "Support", url: `https://docs.google.com/forms/d/e/${process.env.REACT_APP_SUPPORT_FORM_ID}/viewform` },
        { label: "Blog", url: "https://spotlight.stanzacal.com" },
      ],
      secondary: {
        title: "Resources",
        links: [
          { label: "Help Center", url: "https://stanza-1.gitbook.io/stanza-help-center/" },
          { label: "For Creators", url: "/creators" },
          { label: "Pricing and Plans", url: "/creators#pricing" },
        ],
      },
    },
  ];

  /** A footer heading over its stacked links; `mt` spaces a second section within a column. */
  const renderSection = (title, links, mt = 0) => (
    <>
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: 600, mt, mb: 2, color: isDark ? theme.palette.primary.contrastText : theme.palette.text.primary }}
      >
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {links.map((link) => (
          <Link
            key={link.label}
            {...linkPropsFor(link.url)}
            sx={{
              color: isDark ? theme.palette.overlay.white70 : theme.palette.text.secondary,
              textDecoration: "none",
              cursor: "pointer",
              fontSize: "0.875rem",
              "&:hover": { color: isDark ? theme.palette.primary.contrastText : theme.palette.text.primary },
            }}
          >
            {link.label}
          </Link>
        ))}
      </Box>
    </>
  );

  return (
    <Box
      sx={{
        backgroundColor: isDark ? theme.palette.primary.main : theme.palette.paper.secondary,
        borderTop: isDark ? `1px solid ${theme.palette.overlay.white20}` : "none",
        py: { xs: 6, md: 8 },
        px: 2,
      }}
    >
      <Box
        sx={{
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {/* Main Footer Content */}
        <Grid
          container
          spacing={{ xs: 4, md: 6 }}
          sx={{
            mb: { xs: 4, md: 6 },
            justifyContent: { md: "space-between" },
          }}
        >
          {/* Logo and Tagline */}
          <Grid item xs={12} md="auto">
            <Box>
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  mb: 2,
                  cursor: "pointer",
                  display: "inline-block",
                  "& svg": {
                    fill: isDark ? "white" : "black",
                    "& path": {
                      fill: isDark ? "white" : "black",
                    },
                  },
                }}
              >
                <StanzaLogo
                  style={{
                    height: "18px",
                    width: "auto",
                  }}
                />
              </Box>
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? theme.palette.overlay.white70 : theme.palette.text.secondary,
                  lineHeight: 1.6,
                }}
              >
                <Box component="span" sx={{ fontStyle: "italic" }}>
                  The
                </Box>{" "}
                home for your calendars
              </Typography>
            </Box>
          </Grid>

          {/* Footer Columns */}
          {footerColumns.map((column) => (
            <Grid item xs={12} sm={6} md="auto" key={column.title}>
              {renderSection(column.title, column.links)}
              {column.secondary &&
                renderSection(column.secondary.title, column.secondary.links, 4)}
            </Grid>
          ))}
        </Grid>

        {/* Bottom Section */}
        <Box
          sx={{
            pt: 4,
            borderTop: `1px solid ${isDark ? theme.palette.overlay.white20 : theme.palette.divider}`,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: isDark ? theme.palette.overlay.white70 : theme.palette.text.secondary,
            }}
          >
            © {new Date().getFullYear()} SpotOn Computing Inc.
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            <Link
              {...linkPropsFor(getPageRoute("privacy", "PRIVACY"))}
              sx={{
                color: isDark ? theme.palette.overlay.white70 : theme.palette.text.secondary,
                textDecoration: "none",
                cursor: "pointer",
                fontSize: "0.875rem",
                "&:hover": {
                  color: isDark ? theme.palette.primary.contrastText : theme.palette.text.primary,
                },
              }}
            >
              Privacy
            </Link>
            <Link
              {...linkPropsFor(getPageRoute("terms", "TERMS"))}
              sx={{
                color: isDark ? theme.palette.overlay.white70 : theme.palette.text.secondary,
                textDecoration: "none",
                cursor: "pointer",
                fontSize: "0.875rem",
                "&:hover": {
                  color: isDark ? theme.palette.primary.contrastText : theme.palette.text.primary,
                },
              }}
            >
              Terms
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ExpandedFooter;
